// Hideo - global search (Ctrl/Cmd+K, src/components/dialogs/SearchDialog.vue): every hook, video, creator, analysed
// video, source, saved video and script of the profile (all projects), case-insensitive, on titles and texts; mentions are found and
// shown by name. "@type" at the start filters by type, in Italian or English. At most SEARCH_MAX results.
import type { ProfileData } from '../data/types'
import { seriesOf } from './formats'
import { scriptText } from './scripts'
import { savedTitle, savedUrl } from './savedVideos'
import { tv } from '../i18n'

/** Kinds of result */
type SearchKind = 'hook' | 'video' | 'creator' | 'source' | 'saved' | 'script' | 'av'

/** "@type" prefixes (Italian and English). The first prefix that starts with what was typed wins, so the order decides
 *  short prefixes ("@s" = sources) */
const SEARCH_TYPES: Record<string, SearchKind> = {
  hook: 'hook', hooks: 'hook', video: 'video', ped: 'video', creator: 'creator', creators: 'creator', fonte: 'source', fonti: 'source',
  source: 'source', sources: 'source', salvato: 'saved', salvati: 'saved', saved: 'saved', reel: 'saved', script: 'script', analizzato: 'av', analisi: 'av',
  videos: 'video', plan: 'video', scripts: 'script', analysed: 'av', analyzed: 'av', analysis: 'av'
}
/** Result type labels (English sources, shown with t()) */
export const SEARCH_LABELS: Record<SearchKind, string> = { hook: 'Hook', video: 'Video', creator: 'Creator', source: 'Source', saved: 'Saved video', script: 'Script', av: 'Analysed video' }
/** Icon of each kind */
export const SEARCH_ICONS: Record<SearchKind, string> = {
  hook: 'fa-anchor', video: 'fa-clapperboard', creator: 'fa-user-group', source: 'fa-link', saved: 'fa-bookmark', script: 'fa-pen-nib', av: 'fa-magnifying-glass-chart'
}
export const SEARCH_MAX = 25
/** Characters of the text shown under a result's title */
export const SEARCH_TEXT_MAX = 90

export interface SearchResult {
  kind: SearchKind
  title: string
  /** Searched text besides the title (tags, notes…; mentions by name); its start is shown under the title */
  text: string
  /** The item (for 'av': the analysed video) */
  id: string
  /** 'av': the creator it belongs to */
  creatorId?: string
}

/** The query split into the type chosen with "@…" ('' = any) and the text to look for (lower case). An "@" word that is
 *  no type is searched as text; a lone "@" means any type. */
export function parseQuery(query: string): { kind: SearchKind | ''; q: string } {
  let q = query.trim().toLowerCase(), kind: SearchKind | '' = ''
  const m = q.match(/^@(\S*)\s*(.*)$/)
  if (m) {
    const key = m[1] ? Object.keys(SEARCH_TYPES).find(k => k.startsWith(m[1])) : undefined
    kind = key ? SEARCH_TYPES[key] : ''
    if (kind || !m[1]) q = m[2]
  }
  return { kind, q }
}

/** Everything that can be found, in order: hooks, videos, creators (each followed by its analysed videos), sources,
 *  saved videos, scripts. mentionPlain turns @[type:id] mentions into "@Name" (src/features/mentions.ts) */
export function searchItems(d: ProfileData, mentionPlain: (text: string) => string): SearchResult[] {
  const R: SearchResult[] = []
  d.vault.forEach(x => R.push({ kind: 'hook', title: x.title, id: x.id, text: [(x.tags || []).join(' '), mentionPlain(x.content || '')].join(' · ') }))
  d.videos.forEach(x => R.push({ kind: 'video', title: tv(x.title), id: x.id,
    text: [seriesOf(d, x.seriesId)?.name || '', mentionPlain(x.notes || ''), (x.seo && x.seo.kw) || '', (x.seo && x.seo.title) || ''].join(' ') }))
  d.creators.forEach(x => {
    R.push({ kind: 'creator', title: x.name, id: x.id, text: [(x.types || []).join(' '), mentionPlain(x.bio || ''), mentionPlain(x.notes || '')].join(' ') })
    ;(x.analyzedVideos || []).forEach(a => R.push({ kind: 'av', title: a.title, id: a.id, creatorId: x.id, text: mentionPlain(a.analysis || '') }))
  })
  d.sources.forEach(x => R.push({ kind: 'source', title: x.title, id: x.id, text: [(x.tags || []).join(' '), mentionPlain(x.note || '')].join(' · ') }))
  ;(d.savedVideos || []).forEach(x => R.push({ kind: 'saved', title: savedTitle(x), id: x.id, text: [savedUrl(x), mentionPlain(x.note || '')].join(' · ') }))
  // The text of every tab, or the old single text (scriptText, like the rest of the app: before 24/10/2026 a script with an
  // empty list of tabs was not searched in its old text)
  d.scripts.forEach(x => R.push({ kind: 'script', title: x.title, id: x.id, text: scriptText(x) }))
  return R
}

/** The results of a query (first SEARCH_MAX; with nothing typed, the first items) */
export function search(items: readonly SearchResult[], query: string): SearchResult[] {
  const { kind, q } = parseQuery(query)
  return items.filter(r => (!kind || r.kind === kind) && (!q || (r.title + ' ' + (r.text || '')).toLowerCase().includes(q))).slice(0, SEARCH_MAX)
}
